import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronRight, Target, CheckSquare, AlertTriangle, Calendar } from 'lucide-react';
import type { CarePlanResponse } from '@/types/careplan.types';
import { CarePlanStatusConfig } from '@/types/careplan.types';

interface CarePlanCardProps {
  carePlan: CarePlanResponse;
  onClick?: () => void;
  compact?: boolean;
}

export function CarePlanCard({ carePlan, onClick, compact = false }: CarePlanCardProps) {
  const statusConfig = CarePlanStatusConfig[carePlan.status];

  const goalsProgress = carePlan.goalsCount > 0
    ? Math.round((carePlan.goalsAchievedCount / carePlan.goalsCount) * 100)
    : 0;

  const activitiesProgress = carePlan.activitiesCount > 0
    ? Math.round((carePlan.activitiesCompletedCount / carePlan.activitiesCount) * 100)
    : 0;

  if (compact) {
    return (
      <div
        onClick={onClick}
        className={`flex items-center justify-between p-3 hover:bg-muted/50 ${onClick ? 'cursor-pointer' : ''}`}
      >
        <div className="flex items-center gap-3">
          <div
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: statusConfig.color }}
          />
          <div>
            <div className="text-sm font-medium">{carePlan.title}</div>
            <div className="text-xs text-muted-foreground">
              {carePlan.goalsAchievedCount}/{carePlan.goalsCount} metas • {carePlan.activitiesCompletedCount}/{carePlan.activitiesCount} atividades
            </div>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`rounded-lg border bg-card p-4 transition hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{carePlan.title}</h3>
            {carePlan.overdue && (
              <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                <AlertTriangle className="h-3 w-3" />
                Atrasado
              </span>
            )}
          </div>
          {carePlan.description && (
            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
              {carePlan.description}
            </p>
          )}
        </div>
        <span
          className="rounded-full px-2.5 py-0.5 text-xs font-medium"
          style={{
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
          }}
        >
          {statusConfig.label}
        </span>
      </div>

      {/* Progress */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Target className="h-4 w-4" />
            <span>Metas</span>
          </div>
          <div className="mt-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                {carePlan.goalsAchievedCount}/{carePlan.goalsCount}
              </span>
              <span className="text-muted-foreground">{goalsProgress}%</span>
            </div>
            <div className="mt-1 h-2 w-full rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-green-500 transition-all"
                style={{ width: `${goalsProgress}%` }}
              />
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckSquare className="h-4 w-4" />
            <span>Atividades</span>
          </div>
          <div className="mt-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                {carePlan.activitiesCompletedCount}/{carePlan.activitiesCount}
              </span>
              <span className="text-muted-foreground">{activitiesProgress}%</span>
            </div>
            <div className="mt-1 h-2 w-full rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-blue-500 transition-all"
                style={{ width: `${activitiesProgress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          {carePlan.startDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>
                Início: {format(new Date(carePlan.startDate), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            </div>
          )}
          {carePlan.endDate && (
            <div className="flex items-center gap-1">
              <span>
                Fim: {format(new Date(carePlan.endDate), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            </div>
          )}
        </div>
        <span>Por: {carePlan.createdByName}</span>
      </div>
    </div>
  );
}
